import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogModule, DialogService, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { GridModule, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { PagerModule } from '@progress/kendo-angular-pager';
import { MulticheckFilterComponent } from '../filters/multicheck-filter/multicheck-filter.component';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CasiProjectUser, Project, ProjectNew, ProjectType, User as UserModel, CasiProjectLabel } from '../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { AppEnities, blastKeys, CasiCloseOptions, defaultGridPageSize, FormModes } from '../common/constants.lib';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LoggerService } from '../shared/logger.service';
import { ProjectsService } from './projects.service';
import { ToastrService } from 'ngx-toastr';
import { MeService } from '../auth/me.service';
import { faAddressCard, faEdit, faPlusCircle, faTrash, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { setTitle } from '../common/utils.lib';
import { ProjectComponent } from './project/project.component';
import { DeleteConfirmComponent } from '../common-components/delete-confirm/delete-confirm.component';
import { ErrorDialogModalComponent } from '../common-components/error-dialog-modal/error-dialog-modal.component';

import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { ProjectUsersComponent } from './project-users/project-users.component';
import { ActivatedRoute } from '@angular/router';
import { LoadingIndicatorService } from '../shared/loading-indicator.service';
import { SocketService } from '../shared/socket.service';
import {ProjectLabelsComponent} from "./project-labels/project-labels.component";

const { PROJECT } = AppEnities;

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    WindowModule,
    PagerModule,
    GridModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    LayoutModule,
    MulticheckFilterComponent,
    FontAwesomeModule,
    ProjectComponent,
    DeleteConfirmComponent,
    ErrorDialogModalComponent,
    ProjectUsersComponent
  ],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss'
})
export class ProjectsComponent implements OnInit, OnDestroy {
  public rawData: Project[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: Project[] = filterBy(this.rawData, this.filter);

  public currentItem: ProjectType = new ProjectNew();

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = defaultGridPageSize;

  public canUpdate: boolean = false;
  public canCreate: boolean = false;
  public canDestroy: boolean = false;
  public formMode: FormModes = FormModes.NOTSET;
  public editWinOpen: boolean = false;
  public usersWinOpen: boolean = false;
  public labelsWinOpen: boolean = false;

  public users: UserModel[] = [];
  public userAsProjectUser: CasiProjectUser[] = [];

  public labels: CasiProjectLabel[] = [];

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    public dataService: ProjectsService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private loadingIndictorService: LoadingIndicatorService,
    public faIcons: FaIconLibrary,
    private me: MeService,
    private dialogService: DialogService,
    private windowService: WindowService,
    private socketService: SocketService

  ) {
    faIcons.addIcons(faPlusCircle, faAddressCard, faEdit,
      faTrash, faTriangleExclamation);
    faIcons.addIcons(...all);
  }

  public sort: SortDescriptor[] = [
    {
      field: "project_name",
      dir: "asc",
    },
  ];

  //#region Ng Handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...',);
    const { can_Create, can_Update, can_Destroy } = this.me.allICanDo(PROJECT)
    this.canUpdate = can_Update;
    this.canCreate = can_Create;
    this.canDestroy = can_Destroy;
    this.logger.debug('ngOnInit:permissions: ', this.canCreate, can_Create);
    this.loadingIndictorService.loadingOff();

    this._subs.push(this.route.data.subscribe(({
      users, labels
    }) => {
      this.users = users;
      this.labels = labels;
      this.logger.debug(`ngOnInit:users: `, this.users);
      this.logger.debug(`ngOnInit:labels: `, this.labels);

    }));

    this.dataService.query();
    this._subs.push(this.dataService.subscribe(e => {
      // this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
    this._subs.push(this.socketService.onRefresh()
      .subscribe((args: any | unknown) => {
        this.logger.debug(`onRefresh: `, args);
        if (args && args.blastKey === blastKeys.PROJECT_UPDATED) {
          this.dataService.query();
        }
      }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid handlers

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]);
    // return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region view

  onViewUsers(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug(`onViewUsers: `, dataItem);
    this.closeWindowRef();
    this.currentItem = dataItem;
    this.userAsProjectUser = this.users.map(u => CasiProjectUser.createFromUserAndProject(u, (this.currentItem as Project)))
    this.formMode = FormModes.NEW;
    this.showTitle = 'VIEW USERS';
    this._windowRef = this.windowService.open({
      // title: setTitle(this.formMode, 'USER DETAILS'),
      titleBarContent: titleBar,
      content: ProjectUsersComponent,
      width: 900,
      minWidth: 320
    });
    this.setViewUsersInstanceInputs();

    this._windowRef.result.subscribe(results => {
      this.logger.debug('onViewUsers:results: ', results);
      if (results instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('onViewUsers:onClose');
      this.closeWindowRef();
    });
    this.usersWinOpen = true;
  }

  onViewLabels(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug(`onViewLabels: `, dataItem);
    this.closeWindowRef();
    this.currentItem = dataItem;

    this.showTitle = 'VIEW LABELS';
    this._windowRef = this.windowService.open({
      titleBarContent: titleBar,
      content: ProjectLabelsComponent,
      width: 900,
      minWidth: 320
    });
    this.setViewLabelsInstanceInputs();

    this._windowRef.result.subscribe(results => {
      this.logger.debug('onViewLabels:results: ', results);
      if (results instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('onViewLabels:onClose');
      this.closeWindowRef();
    });
    this.labelsWinOpen = true;
  }

  setActive(dataItem: any) {
    this.logger.debug('setActive:dataItem: ', dataItem);
    const { id } = dataItem;
    dataItem.active = !dataItem.active;
    this.dataService.update(dataItem)
      .then(() => {
        this.notify(dataItem.active ? 'Project Activated' : 'Project Deactivated')
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('setActive:error: ', err);
        this.notify("Error Activated/Deactivated Project", true);
      })
  };

  public onAddClicked(): void {
    this.logger.debug("onAddClicked...");
    this.currentItem = new ProjectNew();
    this.formMode = FormModes.NEW;
    this.logger.debug("onAddClicked:currentItem ", this.currentItem);
    this.showTitle = setTitle(this.formMode, 'PROJECT');
    this.editWinOpen = true;
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = Project.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.showTitle = setTitle(this.formMode, 'PROJECT');
    this.editWinOpen = true;
  }

  public onItemSave(args: any): void {
    this.logger.debug('onItemSave...', args);
    this.handleSaveAndClose(args);
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = dataItem;
    this.openConfirmDailog(this.currentItem);
  }

  public close(): void {
    this.logger.debug('close...');
    this.editWinOpen = false;
  }

  viewUsers(dataItem: any): void {
    this.logger.debug("viewUsers: ", dataItem);
    this.currentItem = new Project(dataItem);
    this.usersWinOpen = true;
  }

  public closeUsers(): void {
    this.logger.debug('closeUsers...');
    this.dataService.query();
    this.usersWinOpen = false;
  }

  public openConfirmDailog(dataItem: any): void {
    this.logger.debug("openConfirmDailog: ", dataItem);
    this.currentItem = dataItem;
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 320,
      width: 750,
      // cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete Project - <b>${this.currentItem?.project_name}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentItem);
        this.handleRemove(this.currentItem);
      }
    });
  }

  //#endregion

  //#region Private functions

  private loadData(): void {
    this.logger.debug('loadData...');
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  private handleSaveAndClose(args: any): void {
    this.logger.debug('handleSaveAndClose...', args);
    const { action, data, mode } = args;
    this.dataService.addEdit(data, mode)
      .then(resp => {
        this.notify(mode === FormModes.NEW ? 'Project Added' : 'Project Updated')
        // this.dataService.query();
      })
      .catch(err => {
        this.logger.error('handleSaveAndClose:error: ', err);
        this.notify("Error Adding/Updating Project", true);
      })
      .finally(() => this.editWinOpen = false);
  }

  private handleRemove(args: any): void {
    if (args && !args.id) {
      this.logger.error('handlRemove: No id given');
    }

    this.dataService.destroy(args.id)
      .then(resp => {
        this.logger.debug('handleRemove:resp: ', resp);
        // this.dataService.query();
        this.notify('Project Deleted');
      })
      .catch(err => {
        this.logger.error('handleRemove:err: ', err);
        this.openErrorDailog();
      });
  }

  private openErrorDailog(): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = "Update Issue";
    contentRef.content = `The current project: ${this.currentItem?.project_name} had a problem being removed.`;
    contentRef.buttonText = "Close";
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.usersWinOpen = false;
    this.labelsWinOpen = false;
  }

  private setViewUsersInstanceInputs(): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.project = this.currentItem;
      inputs.users = this.userAsProjectUser;
      this.logger.debug('setViewUsersInstanceInputs: ', inputs);
    }
  }

  private setViewLabelsInstanceInputs(): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.project = this.currentItem;
      inputs.labels = this.labels;
      this.logger.debug('setViewLabelsInstanceInputs: ', inputs);
    }
  }

  //#endregion

}
